import React from 'react';
import { Link } from 'react-router-dom';

import { formatDuration, isVertical, spokenDuration } from '../components/video/videoFormat';
import { SHOWCASE_META, videoPath } from '../components/docs/showcaseData';

/**
 * The landing page's row of three clips (KPR-1930, KPE-062): "See how people
 * use keepr", a poster, title and length per clip, each a link to the clip's
 * own Showcase page, and a link to the Showcase.
 *
 * It lives inside the landing page's rules (pages/Landing.js): plain JSX, no
 * data of its own, no handler, no video element. The clips are handed in by
 * the build-time prerender, which reads the compiled manifest; the posters are
 * the only thing the row fetches from the media host, and lazily. A poster is a
 * frame of its video, one per video, so it does not change with the mode; the
 * row's own colors are tokens and follow `data-mode`.
 *
 * Styles: css/components/public-site.css (.landing__clips).
 */

export const LANDING_CLIPS_TITLE = 'See how people use keepr';
export const LANDING_CLIPS_LINK = 'See every video in Showcase';
const CLIP_COUNT = 3;

/**
 * What is wrong with the configured keys against the compiled manifest's
 * index (`{ videos }`): a list of sentences, each naming its key; empty when
 * the list is empty (no row) or holds three published, non-fixture videos.
 */
export function landingClipProblems(keys, index) {
	const list = Array.isArray(keys) ? keys : [];
	if (!list.length) return [];
	const problems = [];
	if (list.length !== CLIP_COUNT) problems.push(`the landing page shows ${CLIP_COUNT} clips, and src/config/landingClips.js names ${list.length}`);
	const videos = Array.isArray(index?.videos) ? index.videos : [];
	const seen = new Set();
	list.forEach(key => {
		if (seen.has(key)) problems.push(`the landing clip ${key} is named twice in src/config/landingClips.js`);
		seen.add(key);
		const video = videos.find(entry => entry && entry.key === key);
		if (!video) problems.push(`the landing clip ${key} is not in the video manifest (docs/videos/${key}.md)`);
		else if (video.fixture === true) problems.push(`the landing clip ${key} is the test fixture, which is never shown on a public page`);
	});
	return problems;
}

/** The configured clips' manifest records, in the configured order. Throws on any problem. */
export function resolveLandingClips(keys, index) {
	const problems = landingClipProblems(keys, index);
	if (problems.length) throw new Error(problems.join('; '));
	const videos = Array.isArray(index?.videos) ? index.videos : [];
	return (Array.isArray(keys) ? keys : []).map(key => videos.find(entry => entry.key === key));
}

function LandingClip({ video }) {
	const shape = isVertical(video) ? 'vertical' : 'horizontal';
	const length = formatDuration(video.duration);
	const name = [video.title, spokenDuration(video.duration)].filter(Boolean).join(', ');
	return (
		<li className="landing__clip">
			<Link to={videoPath(video.key)} className="landing__clip-link" aria-label={name}>
				<span className={`landing__clip-thumb landing__clip-thumb--${shape}`}>
					{video.poster ? (
						<img className="landing__clip-poster" src={video.poster} alt="" loading="lazy" decoding="async" />
					) : null}
					{length ? <span className="landing__clip-length">{length}</span> : null}
				</span>
				<span className="landing__clip-title">{video.title}</span>
			</Link>
		</li>
	);
}

/** The row, or nothing when there are no clips. */
export default function LandingClips({ clips }) {
	if (!Array.isArray(clips) || !clips.length) return null;
	return (
		<section className="landing__section landing__clips" aria-labelledby="landing-clips">
			<h2 id="landing-clips" className="landing__heading">{LANDING_CLIPS_TITLE}</h2>
			<ul className="landing__clip-list">
				{clips.map(video => <LandingClip key={video.key} video={video} />)}
			</ul>
			<p className="landing__more">
				<Link to={SHOWCASE_META.path}>{LANDING_CLIPS_LINK}</Link>
			</p>
		</section>
	);
}
